import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	forwardRef,
	Input,
	OnInit,
	Output,
	ViewEncapsulation
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { clientSettings } from 'go-modules/models/common/client.settings';
import { UniqueIdGeneratorUtil } from '@go-modules/utilities/unique-id-generator/unique-id-generator.util';
import { RawEditorOptions } from 'hugerte';

interface MaxCharactersExceeded {
	maxExceeded: boolean;
	amountExceededBy: number;
}

@Component({
	selector: 'ngx-rich-text-editor',
	templateUrl: './rich-text-editor.component.html',
	styleUrls: ['./rich-text-editor.component.scss'],
	encapsulation: ViewEncapsulation.None,
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => NgxRichTextEditorComponent),
			multi: true
		}
	],
	standalone: false
})
export class NgxRichTextEditorComponent implements OnInit, ControlValueAccessor {
	@Input() public content: any = null;
	@Input() public placeholder: string;
	@Input() public autoSize: boolean = false;
	@Input() public containerClass: string = '';
	@Input() public autoFocus: boolean = false;
	@Input() public toolbarBelow: string = '';
	@Input() public toolbarOptions: string = 'bold italic underline strikethrough | alignleft aligncenter alignright | bullist numlist';
	@Input() public toolbarGroups: boolean = false;
	@Input() public showTopBorder: boolean = true;
	@Input() public hasRightButton: boolean = false;
	@Output() public enterEvent: EventEmitter<any> = new EventEmitter();
	@Output() public updateEvent: EventEmitter<string> = new EventEmitter();
	@Output() public onEditorFocusOut: EventEmitter<string> = new EventEmitter();
	@Output() public maxCharactersEvent: EventEmitter<MaxCharactersExceeded> = new EventEmitter();

	public hugerteOptions: RawEditorOptions;
	public editorContent: any = null;
	public onChangeFn: any;
	public onTouchedFn: any;
	public maxCharacters: number = clientSettings.CommentTextCharacterLimit;
	public currentEditor: any;
	public uniqueId = null;

	constructor () {
		this.uniqueId = UniqueIdGeneratorUtil.getId();
	}

	public ngOnInit () {
		this.editorContent = this.content;
		const rightPadding = this.hasRightButton ? '40px' : '1px';
		this.hugerteOptions = {
			selector: `#${this.uniqueId} textarea`,
			browser_spellcheck: true,
			menubar: false,
			statusbar: false,
			height: 225,
			plugins: 'lists',
			toolbar: this.toolbarOptions,
			forced_root_block: 'div',
			object_resizing : false,
			placeholder: this.placeholder,
			// sanitizing removes inline styles so we need to preserve the following styles
			// in a different way (semantic or classes)
			formats: {
				underline: { inline: 'u', exact : true },
				alignleft: { block: 'p', classes: 'text-left' },
				aligncenter: { block: 'p', classes: 'text-center' },
				alignright: { block: 'p', classes: 'text-right' },
				strikethrough: { inline: 'del', exact: true }
			},
			content_style: `
				.mce-content-body { font-family: proxima-nova, "Open Sans", sans-serif; font-size: 14px; margin: 8px; min-height: 0; color: #333333; padding-right: `+rightPadding+` !important; }
				.mce-content-body img { border-radius: 10px; width: 100%; max-width: 450px; min-width: 268px; }
				.text-left { text-align: left; }
				.text-center { text-align: center; }
				.text-right { text-align: right; }
			`,
			valid_elements: 'b,strong,i,em,u,del,a[href|target=_blank],ul,ol,li,p[class],br,img[src|alt|width|height|style|class]',
			paste_as_text: false,
			paste_remove_styles: true,
			paste_webkit_styles: 'none',
			paste_preprocess: (_plugin, args) => {
				args.content = this.sanitizeHtmlContent(args.content);
			}
		} as RawEditorOptions;
		if (this.autoSize) {
			this.hugerteOptions.toolbar_location ='bottom';
			this.hugerteOptions.plugins = this.hugerteOptions.plugins + ' autoresize';
			this.hugerteOptions.height = 35;
			this.hugerteOptions.min_height = 35;
			if (this.containerClass) {
				const container = document.querySelector(`.${this.containerClass}`) as HTMLElement;
				const containerHeight = Math.max(35, container.offsetHeight);
				this.hugerteOptions.max_height = Math.min(271, (window.innerHeight * .28), containerHeight);
			} else {
				this.hugerteOptions.max_height = Math.min(271, (window.innerHeight * .28));
			}
			this.hugerteOptions.autoresize_bottom_margin = 0;
		}
		// TODO implement on upgrade to angular13 and tinymce5.2
		if (this.toolbarGroups) {
			this.hugerteOptions.toolbar_groups = {
				formatting: {
					icon: 'more-drawer',
					tooltip: 'More Options',
					items: 'bold italic underline strikethrough | aligncenter alignleft alignright bullist numlist'
				}
			};
		}
	}

	public blurHandler ($event) {
		if (this.editorContent) {
			this.updateEvent.emit(this.sanitizeEditorContent($event.editor));
			this.onChange($event);
		}
	}

	// focusout triggers when an element or *its children* lose focus (unlike blur)
	public focusOutHandler () {
		if (this.currentEditor == null) {
			return;
		}

		const editorContent = this.sanitizeEditorContent(this.currentEditor);
		this.onEditorFocusOut.emit(editorContent);
	}

	public focusHandler ($event) {
		if (this.editorContent) {
			$event.editor.setContent(this.editorContent);
			this.focusOnField();
		}
	}

	public keyupHandler ($event) {
		let node = $event.editor.selection.getNode();
		const nodeName = node.nodeName.toLowerCase();

		// styles applied to text are wrapped in span, this checks if span or styled text belongs under list element
		const isOnListNode = this.isRangeOnListNode(node);

		/*
			TODO: Remove these parts once we've updated tinymce to higher versions
			We should be able to have more options and control on how to alter line breaks behavior
		*/
		// to determine when to add/remove bullets, we check the range
		// (blinking focus " | ") on textarea if its on a list node
		if ($event.event.key === 'Enter' && $event.event.shiftKey && (nodeName === 'li' || isOnListNode)) {
			// tinymce sometimes adds <br> inside li element so we can't determine whether li contains values or not
			// this removes extraneous br content inside the li element
			node.querySelectorAll('br').forEach((element) => {
				element.remove();
			});
			node = $event.editor.selection.getNode();

			// check li content if empty or not
			// remove li if content is empty
			if (node.innerHTML.length > 0) {
				$event.editor.insertContent('<li></li>');

				// when adding/inserting new element, tinymce creates <br> with attr
				// [data-mce-bogus] when an added element is empty
				// this adds an extra list element with <br> into the list
				// instead of adding 1, we have 2 newly inserted li so we should remove the extra one
				/* eslint-disable-next-line max-len */
				// https://stackoverflow.com/questions/48882595/tinymce-br-data-mce-bogus-1-randomly-set-on-empty-textarea
				const bogusElem = $event.editor.getBody().querySelector('li [data-mce-bogus]');
				const listElements = $event.editor.getBody().querySelectorAll('li');
				const emptyElements = Array.from(listElements).find((li: HTMLElement) => {
					return li.childNodes.length === 0;
				});
				if (bogusElem && emptyElements) {
					const listElement = bogusElem.parentElement;
					const range = new Range();
					// since we'll be removing the bogus element, we should return cursor to previous sibling element
					range.setStart(listElement.previousElementSibling, 0);
					range.setEnd(listElement.previousElementSibling, 0);
					$event.editor.selection.setRng(range);
					listElement.remove();
				}
			} else {
				node.remove();
				$event.editor.setContent($event.editor.getContent() + '<p></p>');
				$event.editor.selection.select($event.editor.getBody().lastChild, true);
			}
		}

		this.updateEvent.emit(this.sanitizeEditorContent($event.editor));
		this.onChange($event);
	}

	public keydownHandler ($event) {
		const nodeName = $event.editor.selection.getNode().nodeName.toLowerCase();

		if ($event.event.key === 'Tab' && !$event.event.shiftKey) {
			if (this.focusToolbarButton($event.editor)) {
				$event.event.preventDefault();
				return;
			}
		}

		if ($event.event.key === 'Enter') {
			if (!$event.event.shiftKey) {
				if (nodeName !== 'li') {
					$event.editor.insertContent('<br>');
				}

				// when editing a comment tinymce is destroyed which can cause some errors
				// so allow it time to wrap everything it up first before destroying
				setTimeout(() => {
					this.onChange($event);
					this.enterEvent.emit($event);
				});
			} else if ($event.event.shiftKey && nodeName === 'li') {
				$event.event.preventDefault();
			}
		}
	}

	public initHandler ($event) {
		this.currentEditor = $event.editor;
		this.currentEditor.shortcuts.add('meta+alt+o', 'OrderedList', () => {
			this.currentEditor.execCommand('InsertOrderedList');
		});
		this.currentEditor.shortcuts.add('meta+alt+u', 'UnorderedList', () => {
			this.currentEditor.execCommand('InsertUnorderedList');
		});
		if (this.editorContent) {
			this.currentEditor.setContent(this.editorContent);
		}
		if (this.autoFocus) {
			this.focusOnField();
		}
	}

	public focusOnField () {
		setTimeout(() => {
			this.currentEditor.focus();
			// if content then do trickery to put cursor at end of content
			if (this.editorContent) {
				setTimeout(() => {
					this.currentEditor.focus();
					this.currentEditor.selection.select(this.currentEditor.getBody(), true);
					this.currentEditor.selection.collapse(false);
				});
			}
		});
	}

	/**
	 * Implements ControlValueAccessor
	 */
	public onChange ($event) {
		const content = this.sanitizeEditorContent($event.editor);
		const textContent = $event.editor.getContent({format: 'text'});
		if (this.onChangeFn) {
			const valueToUse = content.length > 0 ? content : textContent;
			this.onChangeFn(valueToUse);
		}
		this.updateCharacterCount(textContent);
	}

	public updateCharacterCount (textContent) {
		if (textContent.length > this.maxCharacters) {
			this.maxCharactersEvent.emit({
				maxExceeded: true,
				amountExceededBy: textContent.length - this.maxCharacters
			});
		} else {
			this.maxCharactersEvent.emit({
				maxExceeded: false,
				amountExceededBy: 0
			});
		}
	}

	public onTouched () {
		if (this.onTouchedFn) this.onTouchedFn();
	}

	public registerOnChange (onChange: any): void {
		this.onChangeFn = onChange;
	}

	public registerOnTouched (onTouched: any): void {
		this.onTouchedFn = onTouched;
	}

	public writeValue (text: string): void {
		if (this.currentEditor) {
			this.currentEditor.setContent(text);
		}
		this.editorContent = text;
	}

	private isRangeOnListNode (node) {
		let isOnListNode = false;
		while(node.parentNode) {
			node = node.parentNode;
			if (node.nodeName.toLowerCase() === 'li') {
				isOnListNode = true;
				break;
			}
		}
		return isOnListNode;
	}

	private focusToolbarButton (editor): boolean {
		const toolbarButton: HTMLButtonElement|null = editor?.editorContainer?.querySelector('.tox-tbtn');
		if (toolbarButton) {
			toolbarButton.focus();
			return true;
		}

		return false;
	}

	private sanitizeHtmlContent (content: string): string {
		const tempDiv = document.createElement('div');
		tempDiv.innerHTML = content;

		this.removeCommentNodes(tempDiv);
		this.removeBase64Images(tempDiv);

		return tempDiv.innerHTML.trim();
	}

	private sanitizeEditorContent (editor): string {
		const content = editor.getContent();
		const sanitizedContent = this.sanitizeHtmlContent(content);
		if (sanitizedContent !== content) {
			editor.setContent(sanitizedContent);
		}

		return sanitizedContent;
	}

	private removeCommentNodes (container: HTMLElement): void {
		const commentNodes: Comment[] = [];
		const treeWalker = document.createTreeWalker(container, NodeFilter.SHOW_COMMENT);
		let currentNode = treeWalker.nextNode();

		while (currentNode != null) {
			commentNodes.push(currentNode as Comment);
			currentNode = treeWalker.nextNode();
		}

		commentNodes.forEach((commentNode) => {
			commentNode.remove();
		});
	}

	private removeBase64Images (container: HTMLElement): void {
		container.querySelectorAll('img').forEach((img) => {
			if (img.src.startsWith('data:image')) {
				img.remove();
			}
		});
	}
}
